import React from "react";
import Modal from "../Modal";
import Icon from "../Icon";
import { displayHandle } from "../../utils/streamHelpers";
import styles from "./DraftPreview.module.sass";

const DraftPreview = ({ visible, onClose, draft, onSend, sending, canSend }) => {
  const { title, recipient, terms } = draft || {};
  const dates = terms?.dates;
  const compensation = terms?.compensation;
  const deliverables = (terms?.deliverables || []).filter((d) => d.title?.trim());
  const goal = terms?.goals?.primaryGoal;
  const notes = terms?.notes;
  const guestCapacity = terms?.guestCapacity;

  const formatGoal = (g) => {
    switch (g) {
      case "increase_bookings": return "Increase Bookings";
      case "increase_following": return "Increase Following";
      case "update_photos": return "Update Photos";
      default: return g;
    }
  };

  const formatCompensation = (c) => {
    switch (c) {
      case "hosted_stay": return "Hosted Stay";
      case "paid": return "Paid Partnership";
      case "negotiable": return "Negotiable";
      default: return c;
    }
  };

  return (
    <Modal visible={visible} onClose={onClose} outerClassName={styles.outer}>
      <div className={styles.preview}>
        <h2 className={styles.heading}>Proposal Preview</h2>
        <p className={styles.subheading}>This is how your proposal will appear to the recipient.</p>

        <div className={styles.card}>
          {/* Recipient header */}
          <div className={styles.cardHeader}>
            {recipient?.avatarUrl ? (
              <img src={recipient.avatarUrl} alt="" className={styles.avatar} />
            ) : (
              <div className={styles.avatarPlaceholder}>
                <Icon name="user" size="24" />
              </div>
            )}
            <div>
              <p className={styles.cardName}>
                {recipient?.handle ? displayHandle(recipient.handle) : recipient?.firstName || "Recipient"}
              </p>
              {recipient?.firstName && recipient?.handle && (
                <p className={styles.cardHandle}>{recipient.firstName}</p>
              )}
            </div>
          </div>

          {/* Title */}
          {title && <h3 className={styles.cardTitle}>{title}</h3>}

          {/* Dates */}
          {dates && (
            <div className={styles.section}>
              <p className={styles.sectionLabel}>Dates</p>
              <p className={styles.sectionValue}>
                {dates.type === "flexible"
                  ? "Flexible dates"
                  : `${dates.start || "TBD"} — ${dates.end || "TBD"}`}
              </p>
            </div>
          )}

          {/* Compensation */}
          {compensation?.type && (
            <div className={styles.section}>
              <p className={styles.sectionLabel}>Compensation</p>
              <p className={styles.sectionValue}>
                {formatCompensation(compensation.type)}
                {compensation.type === "paid" && (compensation.budgetMin || compensation.budgetMax) && (
                  <> — ${compensation.budgetMin || "?"} – ${compensation.budgetMax || "?"}</>
                )}
              </p>
            </div>
          )}

          {/* Guest Capacity */}
          {guestCapacity && (
            <div className={styles.section}>
              <p className={styles.sectionLabel}>Guest Capacity</p>
              <p className={styles.sectionValue}>{guestCapacity} guests</p>
            </div>
          )}

          {/* Deliverables */}
          {deliverables.length > 0 && (
            <>
              <div className={styles.divider} />
              <div className={styles.section}>
                <p className={styles.sectionLabel}>Deliverables ({deliverables.length})</p>
                <div className={styles.deliverablesPreview}>
                  {deliverables.map((d, i) => (
                    <div key={i} className={styles.deliverableTag}>
                      <span className={styles.deliverableDot} />
                      {d.title}
                      {d.description && <span style={{ color: "#94a3b8", marginLeft: 4 }}>— {d.description}</span>}
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}

          {/* Goal */}
          {goal && (
            <div className={styles.section}>
              <p className={styles.sectionLabel}>Primary Goal</p>
              <p className={styles.sectionValue}>{formatGoal(goal)}</p>
            </div>
          )}

          {/* Notes */}
          {notes && (
            <>
              <div className={styles.divider} />
              <div className={styles.section}>
                <p className={styles.sectionLabel}>Additional Notes</p>
                <div className={styles.notesPreview}>{notes}</div>
              </div>
            </>
          )}
        </div>

        <div className={styles.actions}>
          <button className={styles.closeBtn} onClick={onClose}>
            Close Preview
          </button>
          <button
            className={styles.sendBtn}
            onClick={onSend}
            disabled={!canSend || sending}
          >
            <Icon name="send" size="16" />
            {sending ? "Sending..." : "Send Proposal"}
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default DraftPreview;
